/**
 * Synchronous first half of tearing down ngx-extended-pdf-viewer / pdf.js.
 *
 * The library's `ngOnDestroy` already calls `stopRendering()` and later `unbindWindowEvents()`,
 * but both sit behind `await initializationPromise` / `await close()`. MatDialog removes the
 * overlay DOM as soon as `beforeClosed` subscribers return, so a wheel event in that gap hits
 * a detached (or null) container — ISS-197 — and `close()` itself can still reject with
 * ISS-202's `startCleanup: Page N is currently rendering`.
 *
 * Call this from `beforeClosed` *before* `NgxExtendedPdfViewerComponent.ngOnDestroy()`. The
 * methods are the ones the library uses; they are optional because the viewer may not have
 * finished bootstrapping (dialog closed during the loading spinner).
 */

export interface PdfJsViewerApplication {
  pdfViewer?: { stopRendering?: () => void };
  pdfThumbnailViewer?: { stopRendering?: () => void };
  unbindWindowEvents?: () => void;
  unbindEvents?: () => void;
}

/**
 * ngx-extended-pdf-viewer keeps the pdf.js app on `PDFScriptLoaderService.PDFViewerApplication`
 * (taken from the viewer's load event) and never sets a global, so that is the primary source.
 * The `globalThis` read is only a fallback for a pdf.js build that does expose one.
 */
export function pdfJsViewerApplication(loader?: { PDFViewerApplication?: unknown } | null): PdfJsViewerApplication | undefined {
  const fromLibrary = loader?.PDFViewerApplication as PdfJsViewerApplication | undefined;
  return fromLibrary ?? (globalThis as unknown as { PDFViewerApplication?: PdfJsViewerApplication }).PDFViewerApplication;
}

export function preparePdfJsViewerForDestroy(app: PdfJsViewerApplication | undefined | null): void {
  if (!app) {
    return;
  }
  tryCall(() => app.pdfViewer?.stopRendering?.());
  tryCall(() => app.pdfThumbnailViewer?.stopRendering?.());
  // Window-level wheel/keydown handlers must come off while #viewerContainer is still attached.
  tryCall(() => app.unbindWindowEvents?.());
  tryCall(() => app.unbindEvents?.());
}

function tryCall(fn: () => void): void {
  try {
    fn();
  } catch {
    // The singleton is mid-init or already half-destroyed. The caller's ngOnDestroy is next.
  }
}
